import type { RecoveryActionArbiter } from '../../../../../libs/PeerCore/src/streamRecovery/interfaces';
import type { VideoFrameStallState } from './useVideoFrameStall';
import { useEffect, useRef, useState } from 'react';

/**
 * Turns a detected frame stall into one keyframe request.
 *
 * A stall while the peer still reports connected is a picture waiting on a
 * reference frame, usually after wireless loss. Rebuilding the peer connection
 * blanks the session and comes back on the same radio. A transport that is
 * actually down is owned by GuestPeerSession (ICE grace / failed).
 *
 * This hook only acts while the peer still reports connected.
 */

/** Stall must persist this long before a keyframe is requested. */
export const STALL_KEYFRAME_AFTER_MS = 1_500;
const MAX_KEYFRAME_ATTEMPTS = 3;

export type StallEscalationStep = 'idle' | 'keyframe-requested';

export type StallEscalationInput = {
  framesStalled: boolean;
  peerConnected: boolean;
  /** How long frames have been stalled, ms. */
  stalledForMs: number;
};

/**
 * Pure policy. Exported so it is testable without a DOM or timers.
 *
 * Never acts while the peer is disconnected: GuestPeerSession owns recovery in that state.
 */
export function nextStallEscalationStep(
  _current: StallEscalationStep,
  input: StallEscalationInput,
): StallEscalationStep {
  if (!input.framesStalled || !input.peerConnected) return 'idle';
  if (input.stalledForMs < STALL_KEYFRAME_AFTER_MS) return 'idle';
  return 'keyframe-requested';
}

export type StallRecoveryEscalationOptions = {
  stallState?: VideoFrameStallState;
  actionArbiter?: RecoveryActionArbiter;
  /** From useVideoFrameStall. */
  framesStalled: boolean;
  /** Only act while the peer believes it is connected. */
  peerConnected: boolean;
  /** Ask the host for a fresh IDR. Returns false when it could not be sent. */
  requestKeyframe: () => boolean;
  /** Breadcrumb / telemetry sink. */
  onEscalate?: (step: StallEscalationStep, stalledForMs: number) => void;
  nowMs?: () => number;
  pollIntervalMs?: number;
};

/**
 * Poll for a connected frame stall and send one keyframe after 1,500 ms, returning
 * the current step. Failed sends may retry up to three attempts per episode (or
 * continuous observed stall). Structured stall state requires an arbiter. Returns to
 * idle on a poll that observes recovery or disconnection. The timer is cleared on cleanup.
 */
export function useStallRecoveryEscalation({
  stallState,
  actionArbiter,
  framesStalled,
  peerConnected,
  requestKeyframe,
  onEscalate,
  nowMs = () => Date.now(),
  pollIntervalMs = 250,
}: StallRecoveryEscalationOptions): StallEscalationStep {
  const [step, setStep] = useState<StallEscalationStep>('idle');

  const stepRef = useRef<StallEscalationStep>('idle');
  const structuredRef = useRef({ stallState, actionArbiter });
  structuredRef.current = { stallState, actionArbiter };
  const keyframeEpisodeRef = useRef<string | undefined>(undefined);
  const keyframeAttemptsRef = useRef({ episodeId: '', count: 0 });
  const legacyAttemptsRef = useRef(0);
  const stalledSinceRef = useRef<number | null>(null);

  const framesStalledRef = useRef(framesStalled);
  const peerConnectedRef = useRef(peerConnected);
  const requestKeyframeRef = useRef(requestKeyframe);
  const onEscalateRef = useRef(onEscalate);
  framesStalledRef.current = framesStalled;
  peerConnectedRef.current = peerConnected;
  requestKeyframeRef.current = requestKeyframe;
  onEscalateRef.current = onEscalate;

  useEffect(() => {
    const publish = (next: StallEscalationStep) => {
      if (stepRef.current !== next) { stepRef.current = next; setStep(next); }
    };
    const evaluateStructured = (state: VideoFrameStallState, arbiter: RecoveryActionArbiter | undefined) => {
      const id = state.episodeId;
      if (!arbiter || state.state !== 'stalled' || !id || !peerConnectedRef.current) { publish('idle'); return; }
      const next = nextStallEscalationStep(stepRef.current, {
        framesStalled: true, peerConnected: true, stalledForMs: state.stalledForMs,
      });
      if (next === 'keyframe-requested' && keyframeEpisodeRef.current !== id) {
        if (keyframeAttemptsRef.current.episodeId !== id) {
          keyframeAttemptsRef.current = { episodeId: id, count: 0 };
          publish('idle');
        }
        const attempts = keyframeAttemptsRef.current;
        if (attempts.count >= MAX_KEYFRAME_ATTEMPTS) return;
        // Finished action IDs cannot be reused, even when a send failed.
        const keyframeId = attempts.count === 0 ? `${id}:keyframe` : `${id}:keyframe:retry:${attempts.count}`;
        if (!arbiter.tryBegin(keyframeId, 'presentation')) return;
        attempts.count++;
        let sent = false;
        try { sent = requestKeyframeRef.current(); }
        catch { /* A thrown send consumes an attempt, but does not handle the episode. */ }
        arbiter.finish(keyframeId, sent ? 'recovered' : 'failed');
        if (!sent) return;
        keyframeEpisodeRef.current = id;
        onEscalateRef.current?.(next, state.stalledForMs);
      }
      publish(next);
    };
    const evaluate = () => {
      const structured = structuredRef.current;
      if (structured.stallState) { evaluateStructured(structured.stallState, structured.actionArbiter); return; }
      const now = nowMs();
      const stalled = framesStalledRef.current;
      const connected = peerConnectedRef.current;

      if (!stalled || !connected) {
        stalledSinceRef.current = null;
        legacyAttemptsRef.current = 0;
      } else if (stalledSinceRef.current === null) {
        stalledSinceRef.current = now;
      }

      const stalledForMs = stalledSinceRef.current === null ? 0 : now - stalledSinceRef.current;

      const next = nextStallEscalationStep(stepRef.current, {
        framesStalled: stalled,
        peerConnected: connected,
        stalledForMs,
      });

      if (next === 'keyframe-requested' && stepRef.current !== 'keyframe-requested') {
        if (legacyAttemptsRef.current >= MAX_KEYFRAME_ATTEMPTS) return;
        legacyAttemptsRef.current++;
        let sent = false;
        try { sent = requestKeyframeRef.current(); }
        catch { /* Retry on the next poll within the attempt bound. */ }
        if (!sent) return;
        onEscalateRef.current?.(next, stalledForMs);
      }

      if (next !== stepRef.current) {
        stepRef.current = next;
        setStep(next);
      }
    };

    evaluate();
    const id = setInterval(evaluate, pollIntervalMs);
    return () => {
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollIntervalMs]);

  return step;
}
